import { useCallback, useMemo } from 'react'

import { useApolloClient, useMutation, useQuery } from '@apollo/client/react'
import { isBefore, parseISO } from 'date-fns'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useAccount, useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'

export const GetDismissedBanners = graphql(/* GraphQL */ `
  query GetDismissedBanners {
    account {
      id
      dismissedBanners
      createdAt
    }
  }
`)

export const DismissBanner = graphql(/* GraphQL */ `
  mutation DismissBanner($id: String!) {
    dismissBanner(banner: $id) {
      __typename
      ... on DismissBannerResponse {
        success
      }
      ... on DismissBannerError {
        cause
      }
    }
  }
`)

export type DismissableBanner = {
  isActive: boolean
  dismiss: () => void
}

export type DismissableBannerOptions = {
  /**
   * Whether to track the banner dismiss event
   */
  trackDismiss?: boolean
  /**
   * Only show the banner to accounts created on or after this date.
   * Accounts created before this date will never see the banner.
   * This is based on the account's createdAt, not the organization's.
   */
  accountSignUpAfter?: Date
  /**
   * Only show the banner to accounts created before this date.
   * Accounts created on or after this date will never see the banner.
   * This is based on the account's createdAt, not the organization's.
   */
  accountSignUpBefore?: Date
  /**
   * The date after which the banner should no longer be shown.
   * If the current date is past this date, the banner will not be displayed.
   */
  expiryDate?: Date
}

/**
 * Checks if the account was created on or after a given date.
 * @param accountCreatedAt - The account's createdAt timestamp (ISO string from API)
 * @param signUpAfter - The date to compare against
 * @returns true if the account was created on or after the given date, false otherwise
 */
function isSignedUpAfter(
  accountCreatedAt: string | null | undefined,
  signUpAfter: Date,
): boolean {
  if (!accountCreatedAt) {
    return false
  }
  const accountDate = parseISO(accountCreatedAt)
  return !isBefore(accountDate, signUpAfter)
}

/**
 * Checks if the account was created before a given date.
 * @param accountCreatedAt - The account's createdAt timestamp (ISO string from API)
 * @param signUpBefore - The date to compare against
 * @returns true if the account was created before the given date, false otherwise
 */
function isSignedUpBefore(
  accountCreatedAt: string | null | undefined,
  signUpBefore: Date,
): boolean {
  if (!accountCreatedAt) {
    return false
  }
  const accountDate = parseISO(accountCreatedAt)
  return isBefore(accountDate, signUpBefore)
}

/**
 * Checks if the banner has expired based on the expiry date.
 * @param expiryDate - The date after which the banner should no longer be shown
 * @returns true if the current date is past the expiry date, false otherwise
 */
function isExpired(expiryDate: Date): boolean {
  return isBefore(expiryDate, new Date())
}

/**
 * Hook to use dismissable banner, for example, banners, one-time modals, popups, coach marks etc.
 *
 * NOTE: A similar hook exists in app-shell at `domains/app-shell/src/common/hooks/useDismissBanner.ts`.
 * That hook is for internal app-shell use only (not exported). This hook has additional features:
 * - Tracking support via `trackDismiss` option
 * - Account signup date filtering via `accountSignUpAfter` and `accountSignUpBefore` options
 * - Expiry date filtering via `expiryDate` option
 *
 * @param id - id of the banner
 * @param options.trackDismiss - whether to track the banner dismiss event
 * @param options.accountSignUpAfter - only show banner to accounts created on or after this date
 * @param options.accountSignUpBefore - only show banner to accounts created before this date
 * @param options.expiryDate - stop showing the banner after this date
 * @example
 * ```tsx
 * const banner = useDismissableBanner('content-planner-view-coach-mark')
 * if (banner.isActive) {
 *   <Banner onDismiss={banner.dismiss} />
 * }
 *```
 * @example
 * With tracking
 * ```tsx
 * const banner = useDismissableBanner(
 *  'content-planner-view-coach-mark',
 *  { trackDismiss: true },
 * )
 * if (banner.isActive) {
 *   <Banner onDismiss={banner.dismiss} />
 * }
 *```
 * @example
 * Only show banner to accounts created after a certain date
 * ```tsx
 * const banner = useDismissableBanner(
 *  'new-feature-announcement',
 *  { accountSignUpAfter: new Date('2025-01-01') },
 * )
 * if (banner.isActive) {
 *   <Banner onDismiss={banner.dismiss} />
 * }
 *```
 * @example
 * Only show banner to accounts created before a certain date
 * ```tsx
 * const banner = useDismissableBanner(
 *  'legacy-feature-deprecation',
 *  { accountSignUpBefore: new Date('2025-01-01') },
 * )
 * if (banner.isActive) {
 *   <Banner onDismiss={banner.dismiss} />
 * }
 *```
 * @example
 * Stop showing banner after a certain date
 * ```tsx
 * const banner = useDismissableBanner(
 *  'limited-time-offer',
 *  { expiryDate: new Date('2025-12-31') },
 * )
 * if (banner.isActive) {
 *   <Banner onDismiss={banner.dismiss} />
 * }
 *```
 */
export const useDismissableBanner = (
  id: string,
  options?: DismissableBannerOptions,
): DismissableBanner => {
  const { data, refetch } = useQuery(GetDismissedBanners, {
    fetchPolicy: 'cache-first',
  })
  const [dismissBanner] = useMutation(DismissBanner, {
    variables: { id },
    update: (cache) => {
      if (!data?.account) {
        return
      }

      const account = data.account

      // optimistic response
      cache.writeQuery({
        query: GetDismissedBanners,
        data: {
          account: {
            ...account,
            dismissedBanners: [...(account?.dismissedBanners ?? []), id],
          },
        },
      })
    },
  })
  const { account } = useAccount()
  const isImpersonation = account?.isImpersonation === true
  const apolloClient = useApolloClient()
  const organizationId = useOrganizationId()
  const trackingData = useMemo(
    () => ({
      organizationId: organizationId ?? '',
      clientName: 'publishWeb',
      product: 'publish',
      bannerName: id,
    }),
    [organizationId, id],
  )

  const dismissedBanners = data?.account?.dismissedBanners
  const accountCreatedAt = data?.account?.createdAt
  const notDismissed = !!dismissedBanners && !dismissedBanners.includes(id)

  // Check if account was created after the specified date (if provided)
  const meetsSignUpAfterCriteria = options?.accountSignUpAfter
    ? isSignedUpAfter(accountCreatedAt, options.accountSignUpAfter)
    : true

  // Check if account was created before the specified date (if provided)
  const meetsSignUpBeforeCriteria = options?.accountSignUpBefore
    ? isSignedUpBefore(accountCreatedAt, options.accountSignUpBefore)
    : true

  // Check if the banner has expired (if expiry date is provided)
  const hasNotExpired = options?.expiryDate
    ? !isExpired(options.expiryDate)
    : true

  const isActive =
    notDismissed &&
    meetsSignUpAfterCriteria &&
    meetsSignUpBeforeCriteria &&
    hasNotExpired

  const shouldTrackBannerDismiss = !!options?.trackDismiss
  const dismiss = useCallback(async () => {
    if (isImpersonation) {
      // During impersonation, update the local cache only — do NOT persist to the customer's account
      const currentData = apolloClient.cache.readQuery({
        query: GetDismissedBanners,
      })
      if (currentData?.account) {
        apolloClient.cache.writeQuery({
          query: GetDismissedBanners,
          data: {
            account: {
              ...currentData.account,
              dismissedBanners: [
                ...(currentData.account.dismissedBanners ?? []),
                id,
              ],
            },
          },
        })
      }
      return
    }

    if (shouldTrackBannerDismiss) {
      BufferTracker.bannerDismissed(trackingData)
    }

    const response = await dismissBanner()

    if (response.data?.dismissBanner.__typename !== 'DismissBannerResponse') {
      // refetch to get the latest dismissed banners after unsuccessful response
      refetch()
    }
  }, [
    dismissBanner,
    refetch,
    trackingData,
    shouldTrackBannerDismiss,
    isImpersonation,
    apolloClient,
    id,
  ])

  // memoize the banner object to avoid unnecessary changes
  // when used in other hooks/components
  const banner = useMemo(() => ({ isActive, dismiss }), [isActive, dismiss])

  return banner
}
